import React, { useState, useRef } from 'react';
import { useApi } from '../hooks/useApi';

const ALLOWED_EXTENSIONS = {'.jpg': true, '.jpeg': true, '.png': true};
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const getFileIcon = (filename) => {
  return '🖼️';
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const CertificateUploadManager = ({ userId, currentUrls = [], onUpdate, onStatusChange }) => {
  const { request } = useApi();
  const [dragActive, setDragActive] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS[ext]) {
      return `Недопустимый формат: ${ext}. Используйте JPG, PNG, WEBP или PDF.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `Файл слишком большой. Максимум ${formatFileSize(MAX_FILE_SIZE)}, у вас ${formatFileSize(file.size)}.`;
    }
    return null;
  };

  const addFiles = (files) => {
    const newQueue = [];
    for (let file of files) {
      const error = validateFile(file);
      if (error) {
        onStatusChange?.('error', error);
        continue;
      }
      newQueue.push({
        id: Math.random(),
        file,
        status: 'pending',
      });
    }
    setUploadQueue((prev) => [...prev, ...newQueue]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e) => {
    addFiles(Array.from(e.target.files || []));
  };

  const uploadFile = async (queueItem) => {
    try {
      setUploadProgress((prev) => ({...prev, [queueItem.id]: 0}));

      const formData = new FormData();
      formData.append('files', queueItem.file);

      await request(`/api/v1/users/${userId}/certificates`, { method: 'POST', body: formData });

      setUploadProgress((prev) => ({...prev, [queueItem.id]: 100}));
      setUploadQueue((prev) => prev.filter((q) => q.id !== queueItem.id));
      onStatusChange?.('success', `"${queueItem.file.name}" загружен`);
      onUpdate?.();
    } catch (error) {
      onStatusChange?.('error', `Ошибка загрузки "${queueItem.file.name}": ${error.message}`);
      setUploadQueue((prev) => prev.filter((q) => q.id !== queueItem.id));
    }
  };

  const handleUpload = async () => {
    const pending = uploadQueue.filter((q) => q.status === 'pending');
    for (let item of pending) {
      await uploadFile(item);
    }
  };

  const handleRemoveCertificate = async (urlToRemove) => {
    if (!confirm('Удалить этот сертификат?')) return;

    try {
      const newUrls = currentUrls.filter((url) => url !== urlToRemove);
      await request(`/api/v1/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ certificate_urls: newUrls }),
      });
      onStatusChange?.('success', 'Сертификат удалён');
      setTimeout(() => onUpdate?.(), 500);
    } catch (error) {
      console.error('Delete error:', error);
      onStatusChange?.('error', error.message || 'Ошибка удаления сертификата');
    }
  };

  const isImageUrl = (url) => /\.(png|jpe?g|webp)(\?.*)?$/i.test(url || '');

  return (
    <div className="cert-manager">
      <div className="cert-manager__upload">
        <div
          className={`cert-manager__drop-zone ${dragActive ? 'cert-manager__drop-zone--active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="cert-manager__drop-icon">📁</div>
          <p className="cert-manager__drop-text">Перетащите файлы сюда или нажмите</p>
          <p className="cert-manager__drop-hint">Поддержка: JPG, PNG (макс 10MB)</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="cert-manager__select-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Выбрать файлы
          </button>
        </div>

        {uploadQueue.length > 0 && (
          <div className="cert-manager__queue">
            <h5>Очередь загрузки ({uploadQueue.length})</h5>
            {uploadQueue.map((item) => (
              <div key={item.id} className="cert-manager__queue-item">
                <div className="cert-manager__queue-info">
                  <span className="cert-manager__queue-icon">{getFileIcon(item.file.name)}</span>
                  <div className="cert-manager__queue-details">
                    <div className="cert-manager__queue-name">{item.file.name}</div>
                    <div className="cert-manager__queue-size">{formatFileSize(item.file.size)}</div>
                  </div>
                </div>
                {uploadProgress[item.id] !== undefined && uploadProgress[item.id] < 100 && (
                  <div className="cert-manager__progress">
                    <div className="cert-manager__progress-bar">
                      <div
                        className="cert-manager__progress-fill"
                        style={{ width: `${uploadProgress[item.id]}%` }}
                      />
                    </div>
                    <span className="cert-manager__progress-text">{uploadProgress[item.id]}%</span>
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              className="cert-manager__upload-btn"
              onClick={handleUpload}
              disabled={uploadQueue.length === 0}
            >
              Загрузить {uploadQueue.length} {uploadQueue.length === 1 ? 'файл' : 'файлов'}
            </button>
          </div>
        )}
      </div>

      {currentUrls.length > 0 && (
        <div className="cert-manager__gallery">
          <h5>Ваши сертификаты ({currentUrls.length})</h5>
          <div className="cert-manager__gallery-grid">
            {currentUrls.map((url) => (
              <div key={url} className="cert-manager__gallery-item">
                <img src={url} alt="Сертификат" className="cert-manager__gallery-image" />
                <button
                  type="button"
                  className="cert-manager__delete-btn"
                  onClick={() => handleRemoveCertificate(url)}
                  title="Удалить"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateUploadManager;
